import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { CAPErrorCode } from '../models/cap-error.model';
import { ErrorDialogErrorCategory } from '../models/error-dialog.model';
import { FileType } from '../models/file-item.model';

export interface AccessTokens {
    BingAnswers: AccessToken;
    BulkLicensePreview?: AccessToken; // Only available for pwa app for consumer (msa)
    ConsumerShellService: AccessToken;
    Exchange: AccessToken;
    Forms: AccessToken;
    Graph: AccessToken;
    Loki: AccessToken;
    MfsService: AccessToken;
    Mru: AccessToken;
    MyApps: AccessToken;
    Notify: AccessToken;
    OfficeHome?: AccessToken;
    OneDrive: AccessToken;
    OneNote: AccessToken;
    Portal: AccessToken;
    QueryFormulation: AccessToken;
    SharePoint: AccessToken;
    Substrate: AccessToken;
    Sway: AccessToken;
    Ods: AccessToken;
    FluidPushChannel: AccessToken;
    AugLoop: AccessToken;
    TenantFeedback: AccessToken;
    Yammer: AccessToken;
}

// This is also used in Website\msatoken\msa-token.ts. If making changes to this interface
// consider changing there as well.
export interface AccessToken {
    TokenValue: string;
    ExpiryMs?: number;
    HasPermanentError?: boolean;
    HasCAPError?: boolean;
    CAPSubErrorCode?: CAPErrorCode;
    ResourceId?: string;
    Scopes?: Array<string>;
}

/**
 * Standard consumers of access tokens.
 */

export const enum AccessTokenConsumer {
    None = 'None',
    BingAnswers = 'BingAnswers',
    BulkLicensePreview = 'BulkLicensePreview',
    ConsumerShellService = 'ConsumerShellService',
    Exchange = 'Exchange',
    Forms = 'Forms',
    Graph = 'Graph',
    Loki = 'Loki',
    MetaOS = 'MetaOS',
    MfsService = 'MfsService',
    MruPlusPlus = 'MruPlusPlus',
    MyApps = 'MyApps',
    Notify = 'Notify',
    OfficeHome = 'OfficeHome',
    OneDrive = 'OneDrive',
    OneNote = 'OneNote',
    Portal = 'Portal',
    QueryFormulation = 'QueryFormulation',
    SharePoint = 'SharePoint',
    Substrate = 'Substrate',
    Sway = 'Sway',
    WCSS = 'WCSS',
    Ods = 'Ods',
    FluidPushChannel = 'FluidPushChannel',
    AugLoop = 'AugLoop',
    TenantFeedback = 'TenantFeedback',
    Yammer = 'Yammer'
}

export interface AccessTokenInfo {
    refreshEndpoint: string;
    token: AccessToken;
    resourceUrl: string;
    forceRefresh?: boolean; // set to true if you want to force refresh the token on next request.
    pendingRefreshRequest?: AccessTokenRequest;
}

export enum InitializationStatus {
    NotInitialized,
    Initializing,
    Initialized
}

export interface ClientTokenFetchResponse {
    ReauthRequired: boolean;
    LoginUrl: string;
    Tokens: AccessTokens;
}

export interface CAPDialogData {
    category?: ErrorDialogErrorCategory;
    fileType?: FileType;
}

export class AccessTokenResponseError extends Error implements AccessTokenError {
    public readonly statusCode?: number;
    public readonly shouldSwallow: boolean;

    constructor(message: string, statusCode?: number, shouldSwallow = true) {
        super(message);
        this.statusCode = statusCode;
        this.shouldSwallow = shouldSwallow;
    }
}

const AccessTokenDefaultExpiryTimeInMs = 3600000;

@Injectable()
export abstract class AccessTokenService {
    protected errorHandler: OfficeHomePageErrorHandler;
    protected _reauth: Subject<void>;

    private defaultTokenFormat: AccessTokenFormat;
    private defaultTokenSource: AccessTokenSource;

    // NOTE: Angular throws error on unresolved constructor params in injectable class, so we can't pass these in constructor
    protected init(
        errorHandler: OfficeHomePageErrorHandler,
        defaultTokenFormat: AccessTokenFormat,
        defaultTokenSource: AccessTokenSource
    ) {
        this.errorHandler = errorHandler;
        this.defaultTokenFormat = defaultTokenFormat;
        this.defaultTokenSource = defaultTokenSource;

        this._reauth = new Subject();
        this.reauthComplete = this._reauth.asObservable();
    }

    protected getDefaultExpiry() {
        return new Date().getTime() + AccessTokenDefaultExpiryTimeInMs;
    }

    // NOTE: Never rejects. Always resolves with an object indicating success
    // Keeping init add the top to maintain consistency with constructors
    // tslint:disable-next-line:member-ordering
    public async getAccessToken(
        tokenConsumer: AccessTokenConsumer,
        forceFetch?: boolean,
        resourceUrl?: string,
        allowInteractive?: boolean,
        capDialogData?: CAPDialogData,
        clientId?: string,
        scope?: string,
        claims?: Array<string>,
        claimsChallenge?: string
    ): Promise<AccessTokenResponse> {
        if (!tokenConsumer) {
            return Promise.resolve({ success: false, token: '', format: this.defaultTokenFormat, source: this.defaultTokenSource, error: new Error('invalid tokenConsumer') });
        }

        return this.getAccessTokenResponse(tokenConsumer, forceFetch, resourceUrl, allowInteractive, capDialogData, clientId, scope, claims, claimsChallenge).then(tokenResponse => {
            if (!tokenResponse || !tokenResponse.token) {
                return { success: false, token: '', format: this.defaultTokenFormat, source: this.defaultTokenSource, error: new Error('Empty token returned') };
            }
            return tokenResponse;
        }).catch(error => {
            this.errorHandler.handleError(error, ErrorId.SuNwZ);
            return { success: false, token: '', format: this.defaultTokenFormat, source: this.defaultTokenSource, error };
        });
    }

    // Keeping init add the top to maintain consistency with constructors
    // tslint:disable-next-line:member-ordering
    public abstract hasPermanentAuthError(): Promise<boolean>;

    // tslint:disable-next-line:member-ordering
    public abstract hasOneDriveToken(): boolean;

    // Checks if given token has a CAP error stored in cache.
    // tslint:disable-next-line:member-ordering
    public abstract hasCAPError(tokenConsumer: AccessTokenConsumer, resourceUrl?: string): boolean;

    // Subscribe to get notified when the user reauthenticates in the session.
    // This can be used as a trigger to clear any errors that were cached due to CAP errors
    // or some other auth related error.
    public reauthComplete: Observable<void>;

    // Must return a Promise with a valid AccessTokenResponse
    protected abstract getAccessTokenResponse(
        tokenConsumer: AccessTokenConsumer,
        forceFetch?: boolean,
        resourceUrl?: string,
        allowInteractive?: boolean,
        capDialogData?: CAPDialogData,
        clientId?: string,
        scope?: string,
        claims?: Array<string>,
        claimsChallenge?: string
    ): Promise<AccessTokenResponse>;
}
